import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * GMC Sierra LD (linkId 2379).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2379.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the GMC palette + `Stratum GMC` face verbatim
 * from 2385/2382/2377.
 *
 * 1.0 ships a REAL `variant` filter here (relabelled `cabType`, `key`
 * 'variant') matching the actual Crew Cab / Regular Cab split — unlike
 * every other GMC project so far, whose engine/transmission filters are
 * generic unrelated boilerplate. Kept, and CONFIG is left visible because
 * of it (every other GMC migration hides it as empty).
 * 
 * Regular Cab variants share the bulk of their configurationIds with their
 * Crew Cab counterpart (AT4 <-> AT4 Regular Cab: 20/24 accessories shared;
 * SLE <-> SLE Regular Cab: all 29 shared) — a real, deliberate content
 * overlap in 1.0, not a data defect; consolidate-or-split handles it the
 * same as everywhere else.
 * 
 * Pricing: 1.0 ships all-zero/placeholder pricing for every variant
 * (showPrice:false in 1.0's own presentation config) — featureFlags.
 * showPrices is off and specifications.price is left blank rather than
 * showing a misleading "Starting from 0 MSRP".
 *
 * Dropped: 1.0's `engine`/`transmission` filterOptions (generic
 * Petrol/Turbo Petrol, CVT/MT/AMT boilerplate found across every GMC project
 * so far, unrelated to any real trim distinction here). `toggleTabs` hides
 * CONFIG (no real filters) — 2384 precedent.
 *
 * Assets live in public/assets/{images,videos}/2379/, resolved by
 * `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Sierra: {
      variants: {
        'denali-ultimate': {
          configurationId: 'mlfv94crqnmb',
          name: {
            en: 'Denali Ultimate',
            ar: 'دينالي Ultimate',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'Denali Ultimate',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/Denali_Ultimate/DU_Onyx_Black_var.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            OMR: 0.1,
            BHD: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 0,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Crew Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '5SB',
          brandItemCode: 'GCSILD07',
        },
        denali: {
          configurationId: 'mlfv94crtqnmb',
          name: {
            en: 'Denali',
            ar: 'دينالي',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'Denali',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/Denali/denali_variant.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            OMR: 0.1,
            BHD: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Crew Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '5SA',
          brandItemCode: 'GCSILD05',
        },
        at4x: {
          configurationId: 'mlfv94ccrtqnmb',
          name: {
            en: 'AT4X',
            ar: 'AT4X',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'AT4X',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/AT4X/at4x_variant.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            OMR: 0.1,
            BHD: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 2,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Crew Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '4SG',
          brandItemCode: 'GCSILD06',
        },
        at4: {
          configurationId: 'mlfv94ccrjtqnmb',
          name: {
            en: 'AT4',
            ar: 'AT4',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'AT4',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/AT4/at4_variant.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            OMR: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 3,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Crew Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '4SB',
          brandItemCode: 'GCSILD04',
        },
        elevation: {
          configurationId: 'mlfv99ccrjtqnmb',
          name: {
            en: 'Elevation',
            ar: 'Elevation',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'Elevation',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/Ele/elevation_variant_cc.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            OMR: 0.1,
            BHD: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 4,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Crew Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '3SB',
          brandItemCode: 'GCSILD03',
        },
        sle: {
          configurationId: 'mlfv99corjtqnmb',
          name: {
            en: 'SLE',
            ar: 'SLE',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'SLE',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/SLE/sle_variant_cc.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            OMR: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 5,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Crew Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '3SA',
          brandItemCode: 'GCSILD01',
        },
        'at4-regular-cab': {
          configurationId: 'mlfv97654ccrjtqnmb',
          name: {
            en: 'AT4 Regular Cab',
            ar: 'AT4 بالمقصورة العادية',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'AT4 Regular Cab',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/AT4/at4_variant_rc.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            OMR: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 6,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Regular Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '4SB',
          brandItemCode: 'GCSILD04',
        },
        'elevation-regular-cab': {
          configurationId: 'mlfv99c00crjtqnmb',
          name: {
            en: 'Elevation Regular Cab',
            ar: 'Elevation بالمقصورة العادية',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'Elevation Regular Cab',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/Ele/elevation_variant_rc.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            OM3: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 7,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Regular Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '3SB',
          brandItemCode: 'GCSILD03',
        },
        'sle-regular-cab': {
          configurationId: 'mlfvbh99corjtqnmb',
          name: {
            en: 'SLE Regular Cab',
            ar: 'SLE بالمقصورة العادية',
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: '4WD',
          trim: 'SLE Regular Cab',
          highlights: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: 'Torque 624 NM',
              ar: 'عزم الدوران 624 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'V8 محرك 6.2 لتر',
            },
          ],
          specs: [
            {
              en: '420 HP',
              ar: '420 حصاناً',
            },
            {
              en: '624 NM',
              ar: '624 نيوتن متر',
            },
          ],
          image: 'images/2379/SLE/sle_variant_rc.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            OMR: 0.1,
            KW: 0.1,
            BHD: 0.1,
            IQD: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 8,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          cabType: 'Regular Cab',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '3SA',
          brandItemCode: 'GCSILD01',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2379/gcc_desert_env.png',
          title: {
            en: 'Desert Landscape 1',
            ar: '1 المناظر الطبيعية الصحراوية',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2379/goegap.jpg',
          title: {
            en: 'Desert Landscape 2',
            ar: '2 المناظر الطبيعية الصحراوية',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          iswqr3ta2smz: {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/Denali_Ultimate/DU_Titanium_Rush_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          '9dwvdtcubmni': {
            label: {
              en: 'Downpour Metallic',
              ar: 'مطري معدني',
            },
            image: 'images/2379/Denali_Ultimate/DU_Downpour_Metallic.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX21',
          },
          rvbult7msus2: {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/Denali_Ultimate/Coastal_Dune.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          ldux7w9zx2tr: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/Denali_Ultimate/DU_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          ajnl9x8ueak8: {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/Denali_Ultimate/DU_Onyx_Black.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          hqo2209i14mn: {
            label: {
              en: 'Glacier White Tricoat',
              ar: 'أبيض جليدي بثلاث طبقات',
            },
            image: 'images/2379/Denali_Ultimate/Glacier_White_Tricoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX76',
          },
          ngqz3k7xopn9: {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/Denali/denali_Titanium_Rush_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          rn1y9hhalv1a: {
            label: {
              en: 'Downpour Metallic',
              ar: 'مطري معدني',
            },
            image: 'images/2379/Denali/denali_Downpour_Metallic.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX21',
          },
          '2wpu0f9z0xy2': {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/Denali/denali_Coastal_Dune.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          '31lc3bcuz6ga': {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/Denali/denali_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          w8tm66d7rc8h: {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/Denali/denali_Onyx_Black.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          uenro76tzzk2: {
            label: {
              en: 'Glacier White Tricoat',
              ar: 'أبيض جليدي بثلاث طبقات',
            },
            image: 'images/2379/Denali/denali_Glacier_White_Tricoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 11,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX76',
          },
          un2u7cg5fike: {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/AT4X/at4x_Titanium_Rush_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 12,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          rr3ex8ma7rbb: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2379/AT4X/at4x_Summit_White.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 13,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '93y3jy3mvdzv': {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/AT4X/at4x_Coastal_Dune.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 14,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          '1gk32exc74gx': {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/AT4X/at4x_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 15,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          abc6emjqxsgo: {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/AT4X/at4x_Onyx_Black.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 16,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          bz0iajvp7zaq: {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2379/AT4X/at4x_Volcanic_Red_Tintcoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 17,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          l2cnhaxojfbj: {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/AT4/at4_Titanium_Rush_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 18,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          'l2cnhaxojfbj-at4-regular-cab': {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/AT4/at4_Titanium_Rush_Metallic_rc.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 19,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          xi1iswcatix1: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2379/AT4/at4_Summit_White.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 20,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          'xi1iswcatix1-at4-regular-cab': {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2379/AT4/at4_Summit_White_rc.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 21,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '59f9uyd0u1rn': {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/AT4/at4_Coastal_Dune.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 22,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          '59f9uyd0u1rn-at4-regular-cab': {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/AT4/at4_Coastal_Dune_rc.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 23,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          '7yhc7i937bni': {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/AT4/at4_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 24,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          '7yhc7i937bni-at4-regular-cab': {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/AT4/at4_Sterling_Metallic_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 25,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          a1kpp98rucik: {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/AT4/at4_Onyx_Black.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 26,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          'a1kpp98rucik-at4-regular-cab': {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/AT4/at4_Onyx_Black_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 27,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          '2wbo981nokwr': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2379/AT4/at4_Volcanic_Red_Tintcoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 28,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          '2wbo981nokwr-at4-regular-cab': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2379/AT4/at4_Volcanic_Red_Tintcoat_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 29,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          hxlzuoifin7b: {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/Ele/ele_Titanium_Rush_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 30,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          'hxlzuoifin7b-elevation-regular-cab': {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/Ele/ele_Titanium_Rush_Metallic_rc.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 31,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          inrrvsldvwfa: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2379/Ele/ele_Summit_White.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 32,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          'inrrvsldvwfa-elevation-regular-cab': {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2379/Ele/ele_Summit_White_rc.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 33,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '0ad0ux6wzz1e': {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/Ele/ele_Coastal_Dune.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 34,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          '0ad0ux6wzz1e-elevation-regular-cab': {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/Ele/ele_Coastal_Dune_rc.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 35,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          xrb4gsuqlnnn: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/Ele/ele_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 36,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          'xrb4gsuqlnnn-elevation-regular-cab': {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/Ele/ele_Sterling_Metallic_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 37,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          ok9cu5jrbr38: {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/Ele/ele_Onyx_Black.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 38,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          'ok9cu5jrbr38-elevation-regular-cab': {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/Ele/ele_Onyx_Black_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 39,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          aqvfqfntmzdj: {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2379/Ele/ele_volcanic_Red.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 40,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          'aqvfqfntmzdj-elevation-regular-cab': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2379/Ele/ele_volcanic_Red_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 41,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          '7eez7nplog7a': {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/SLE/SLE_Titanium_Rush_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 42,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          '7eez7nplog7a-sle-regular-cab': {
            label: {
              en: 'Titanium Rush Metallic',
              ar: 'تيتانيوم راش ميتاليك',
            },
            image: 'images/2379/SLE/sle_Titanium_Rush_Metallic_rc.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 43,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX16',
          },
          dtjmzz3bsfrl: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2379/SLE/SLE_Summit_White.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 44,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          'dtjmzz3bsfrl-sle-regular-cab': {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2379/SLE/sle_Summit_White_rc.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 45,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          ruym5z4zk7bp: {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/SLE/SLE_Coastal_Dune.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 46,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          'ruym5z4zk7bp-sle-regular-cab': {
            label: {
              en: 'Coastal Dune',
              ar: 'لون رمال الساحل',
            },
            image: 'images/2379/SLE/sle_Coastal_Dune_rc.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 47,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX64',
          },
          svfkt34o1vbs: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/SLE/SLE_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 48,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          'svfkt34o1vbs-sle-regular-cab': {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي ستيرلينج معدني',
            },
            image: 'images/2379/SLE/sle_Sterling_Metallic_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 49,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          '2h6rvjratd39': {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/SLE/SLE_Onyx_Black.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 50,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          '2h6rvjratd39-sle-regular-cab': {
            label: {
              en: 'Onyx Black',
              ar: 'أسود أونيكس',
            },
            image: 'images/2379/SLE/sle_Onyx_Black_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 51,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX17',
          },
          ys6fc5ecm0gr: {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2379/SLE/SLE_Volcanic_Red.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 52,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          'ys6fc5ecm0gr-sle-regular-cab': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2379/SLE/sle_Volcanic_Red_rc.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 53,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
        },
        interior: {
          wghqimbkxd6j: {
            label: {
              en: 'Alpine Umber Full Grain Leather Seats',
              ar: 'مقاعد من الجلد كامل الحبيبات بلون Alpine Umber',
            },
            image: 'images/2379/Denali_Ultimate/DU_Interior_color.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H40',
            brandItemCode: 'IN35',
          },
          '1lyzdw5is5pe': {
            label: {
              en: 'Atmosphere/ Brownstone Forge Perforated Leather Seating',
              ar: 'مقاعد جلدية مثقبة Forge باللونين بيج/حجري بني',
            },
            image: 'images/2379/Denali/denali_INT.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H24',
            brandItemCode: 'IN36',
          },
          z3kg3f2fz96g: {
            label: {
              en: 'Obsidian Rush with Full Grain Leather seats',
              ar: 'أوبسيديان راش مع مقاعد جلدية كاملة الحبيبات',
            },
            image: 'images/2379/AT4X/at4x_INT.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H9J',
            brandItemCode: 'IN68',
          },
          icae047aermx: {
            label: {
              en: 'Jet Black with Kalahari accents',
              ar: 'أسود داكن مع لمسات بلون كالاهاري',
            },
            image: 'images/2379/AT4/at4_INT.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 3,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVD',
            brandItemCode: 'IN54',
          },
          'icae047aermx-at4-regular-cab': {
            label: {
              en: 'Jet Black with Kalahari accents',
              ar: 'أسود داكن مع لمسات بلون كالاهاري',
            },
            image: 'images/2379/AT4/at4_INT_rc.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 4,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVD',
            brandItemCode: 'IN54',
          },
          '77v7ehz8c21y': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'قماش جيديون / بيج داكن جداً',
            },
            image: 'images/2379/Ele/ele_INT_Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 5,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN27',
          },
          '77v7ehz8c21y-elevation-regular-cab': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'قماش جيديون / بيج داكن جداً',
            },
            image: 'images/2379/Ele/ele_INT_Cloth_rc.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 6,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN27',
          },
          '5vktfxushcn42': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Leather',
              ar: 'جلد جيديون / بيج داكن جداً',
            },
            image: 'images/2379/Ele/ele_INT_Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 7,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN25',
          },
          '5vktfxushcn42-elevation-regular-cab': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Leather',
              ar: 'جلد جيديون / بيج داكن جداً',
            },
            image: 'images/2379/Ele/ele_INT_Leather_rc.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 8,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN25',
          },
          '5vktfxuscn42': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'قماش جيديون / بيج داكن جداً',
            },
            image: 'images/2379/SLE/INT.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 9,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN27',
          },
          '5vktfxuscn42-sle-regular-cab': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'قماش جيديون / بيج داكن جداً',
            },
            image: 'images/2379/SLE/rc_INT.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 10,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN27',
          },
        },
      },
      features: {
        ovv9q52phjv5: {
          label: {
            en: 'Chrome Grille',
            ar: 'شبك أمامي من الكروم',
          },
          image: 'images/2379/Denali_Ultimate/DU_Front_Grille.jpg',
          title: {
            en: 'Chrome Grille',
            ar: 'شبك أمامي من الكروم',
          },
          desc: {
            en: 'Signature Denali Vade Chrome grille',
            ar: 'شبك كروم Vade الخاص بطراز دينالي',
          },
          type: 'exterior',
        },
        f9g0udkfgg1s: {
          label: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          image: 'images/2379/Denali_Ultimate/DU_Wheels.jpg',
          title: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          desc: {
            en: '22" gloss-black painted aluminum wheels with machined accents',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود اللامع مقاس 22 إنش مع لمسات مشغولة',
          },
          type: 'exterior',
        },
        aqhtccsq854s: {
          label: {
            en: 'Power sunroof',
            ar: 'فتحة سقف كهربائية',
          },
          image: 'images/2379/Denali_Ultimate/DU_Power_Sunroof.jpg',
          title: {
            en: 'Power sunroof',
            ar: 'فتحة سقف كهربائية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        uuwg2vhs5qmz: {
          label: {
            en: '6.2L V8 Engine with 420 HP',
            ar: 'محرك V8 سعة 6.2 لتر بقوة 420 حصان',
          },
          image: 'images/2379/Denali_Ultimate/DU_Engine.jpg',
          title: {
            en: '6.2L V8 Engine with 420 HP',
            ar: 'محرك V8 سعة 6.2 لتر بقوة 420 حصان',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '422mk8nah7jh': {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/Denali_Ultimate/DU_Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        k0yx9f0088x6: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/Denali_Ultimate/DU_DIC.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        '3ofodapu2ddp': {
          label: {
            en: 'Head-up Display',
            ar: 'شاشة عرض علوية أمامية',
          },
          image: 'images/2379/Denali_Ultimate/DU_HUD.jpg',
          title: {
            en: 'Head-up Display',
            ar: 'شاشة عرض علوية أمامية',
          },
          desc: {
            en: '15" diagonal multicolor Head-Up Display',
            ar: 'شاشة عرض علوية أمامية متعددة الألوان مقاس 15 إنش',
          },
          type: 'interior',
        },
        '2xmnp8rwibts': {
          label: {
            en: 'Apline Umber Interiors',
            ar: 'لمسات داخلية بلون Alpine Umber',
          },
          image: 'images/2379/Denali_Ultimate/DU_Interior.jpg',
          title: {
            en: 'Apline Umber Interiors',
            ar: 'لمسات داخلية بلون Alpine Umber',
          },
          desc: {
            en: 'Ultimate Alpine Umber interior featuring full-grain leather seating and authentic open-pore paldao wood',
            ar: 'تصميم داخلي فاخر بلون Alpine Umber يتميز بمقاعد من الجلد المحبب بالكامل وخشب paldao الأصلي ذي المسام المفتوحة',
          },
          type: 'interior',
        },
        hq5xn1dxclar: {
          label: {
            en: 'Denali Summit Seat Pattern',
            ar: 'نمط مقاعد دينالي سوميت',
          },
          image: 'images/2379/Denali_Ultimate/DU_Seat_Pattern.jpg',
          title: {
            en: 'Denali Summit Seat Pattern',
            ar: 'نمط مقاعد دينالي سوميت',
          },
          desc: {
            en: 'Topographical map patterns Mt. Denalis summit embossed and laser etched across the interior',
            ar: 'نقوش خرائط طبوغرافية لقمة جبل دينالي، منفّذة بالنقش البارز والحفر بالليزر عبر المقصورة',
          },
          type: 'interior',
        },
        qvs3dqxessnb: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2379/Denali_Ultimate/DU_Bose_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '12-Speaker Bose® Premium series with Centerpoint® surround sound',
            ar: 'سلسلة Bose® Premium المكونة من 12 مكبر صوت مع صوت محيطي Centerpoint®',
          },
          type: 'interior',
        },
        ie634vsmq0j6: {
          label: {
            en: 'Chrome Grille',
            ar: 'شبك أمامي من الكروم',
          },
          image: 'images/2379/Denali/Front_Grille.jpg',
          title: {
            en: 'Chrome Grille',
            ar: 'شبك أمامي من الكروم',
          },
          desc: {
            en: 'Signature Denali Chrome grille',
            ar: 'شبك أمامي من الكروم خاص بطراز دينالي',
          },
          type: 'exterior',
        },
        '3ihc3y18u7zd': {
          label: {
            en: 'Polished Aluminum wheels',
            ar: 'عجلات ألومنيوم مصقولة',
          },
          image: 'images/2379/Denali/Wheels.jpg',
          title: {
            en: 'Polished Aluminum wheels',
            ar: 'عجلات ألومنيوم مصقولة',
          },
          desc: {
            en: '20" Multi dimenstional polished aluminum wheels',
            ar: 'عجلات ألومنيوم مصقولة متعددة الأبعاد مقاس 20 إنش',
          },
          type: 'exterior',
        },
        '6q2cg6iiqn3h': {
          label: {
            en: 'Power sunroof',
            ar: 'فتحة سقف كهربائية',
          },
          image: 'images/2379/Denali/Power_Sunroof.jpg',
          title: {
            en: 'Power sunroof',
            ar: 'فتحة سقف كهربائية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        ynodrohzcw0i: {
          label: {
            en: '6.2L V8 Engine with 420 HP',
            ar: 'محرك V8 سعة 6.2 لتر بقوة 420 حصان',
          },
          image: 'images/2379/Denali/6_2L_Engine.jpg',
          title: {
            en: '6.2L V8 Engine with 420 HP',
            ar: 'محرك V8 سعة 6.2 لتر بقوة 420 حصان',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        jf4fjyd9qle0: {
          label: {
            en: 'Retractable Power steps',
            ar: 'عتبات كهربائية قابلة للطي',
          },
          image: 'images/2379/Denali/Retractable_Power_Steps.jpg',
          title: {
            en: 'Retractable Power steps',
            ar: 'عتبات كهربائية قابلة للطي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        givzqghx1l82: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/Denali/Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        bz0qkr3fp1sc: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/Denali/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        m0ndpn2lkd0f: {
          label: {
            en: 'Authentic Open Pore Wood Trim',
            ar: 'تطعيمات خشبية أصلية ذات مسام مفتوحة',
          },
          image: 'images/2379/Denali/Interior.jpg',
          title: {
            en: 'Authentic Open Pore Wood Trim',
            ar: 'تطعيمات خشبية أصلية ذات مسام مفتوحة',
          },
          desc: {
            en: 'Authentic open pore wood, brushed aluminum rim and forged perforated leather seating surfaces',
            ar: 'خشب طبيعي ذو مسام مفتوحة، وحافة من الألومنيوم المصقول، ومقاعد مكسوة بجلد Forge المثقب',
          },
          type: 'interior',
        },
        m52hpo7jkq9e: {
          label: {
            en: 'Wireless center console charging',
            ar: 'شحن لاسلكي في الكنسولة المركزية',
          },
          image: 'images/2379/Denali/Wireless_charging.jpg',
          title: {
            en: 'Wireless center console charging',
            ar: 'شحن لاسلكي في الكنسولة المركزية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        b5uhkotwnzjn: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2379/Denali/Bose_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '12-Speaker Bose® Premium series with Centerpoint® surround sound',
            ar: 'سلسلة Bose® Premium المكونة من 12 مكبر صوت مع صوت محيطي Centerpoint®',
          },
          type: 'interior',
        },
        ptmckza3t31n: {
          label: {
            en: 'Front Bumper',
            ar: 'المصد الأمامي',
          },
          image: 'images/2379/AT4X/Front_Bumper.jpg',
          title: {
            en: 'Front Bumper',
            ar: 'المصد الأمامي',
          },
          desc: {
            en: 'AEV stamped steel front bumper with front winch capability and stamped boron steel front approach AEV skid plate',
            ar: 'مصد أمامي من الفولاذ المختوم من شركة AEV مع إمكانية تركيب ونش أمامي، ولوحة حماية سفلية أمامية من فولاذ البورون المختوم من شركة AEV',
          },
          type: 'exterior',
        },
        nv6lzeqsok8w: {
          label: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          image: 'images/2379/AT4X/Wheels.jpg',
          title: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          desc: {
            en: '18" Painted aluminum with dark painted pockets',
            ar: 'عجلات ألومنيوم مطلية مقاس 18 إنش مع جيوب مطلية باللون الداكن',
          },
          type: 'exterior',
        },
        fpi9zqhbdcvv: {
          label: {
            en: 'Power sunroof',
            ar: 'فتحة سقف كهربائية',
          },
          image: 'images/2379/AT4X/Power_Sunroof.jpg',
          title: {
            en: 'Power sunroof',
            ar: 'فتحة سقف كهربائية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        e31dagcaee09: {
          label: {
            en: '6.2L V8 Engine with 420 HP',
            ar: 'محرك V8 سعة 6.2 لتر بقوة 420 حصان',
          },
          image: 'images/2379/AT4X/6_2L_Engine.jpg',
          title: {
            en: '6.2L V8 Engine with 420 HP',
            ar: 'محرك V8 سعة 6.2 لتر بقوة 420 حصان',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        lkuhj3fvxjx9: {
          label: {
            en: 'Multipro tailgate',
            ar: 'باب خلفي MultiPro',
          },
          image: 'images/2379/AT4X/Tailgate.jpg',
          title: {
            en: 'Multipro tailgate',
            ar: 'باب خلفي MultiPro',
          },
          desc: {
            en: 'Six-Function Multipro tailgate',
            ar: 'باب خلفي MultiPro بستّ وظائف',
          },
          type: 'exterior',
        },
        gk2otd6c69sm: {
          label: {
            en: '2" Suspension Lift',
            ar: 'رفع نظام التعليق بمقدار 2 إنش',
          },
          image: 'images/2379/AT4X/2_inch_Suspension_Lift.jpg',
          title: {
            en: '2" Suspension Lift',
            ar: 'رفع نظام التعليق بمقدار 2 إنش',
          },
          desc: {
            en: '2" factory lift with off-road suspension and MULTIMATIC DSSV shocks',
            ar: 'رفع مصنعي 2 إنش مع تعليق للطرق الوعرة وممتصات صدمات MULTIMATIC  DSSV',
          },
          type: 'exterior',
        },
        tnagptikzt93: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/AT4X/Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        '3eo3tzxeu3a8': {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/AT4X/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        tjazpuwqk701: {
          label: {
            en: 'Obsidian Rush Interior',
            ar: 'تصميم داخلي أوبسيديان راش',
          },
          image: 'images/2379/AT4X/Interior.jpg',
          title: {
            en: 'Obsidian Rush Interior',
            ar: 'تصميم داخلي أوبسيديان راش',
          },
          desc: {
            en: 'Obsidian full-grain leather seating with technical grain leather accents and authentic Vanta Ash Wood',
            ar: 'مقاعد من جلد أوبسيديان الطبيعي مع لمسات من الجلد الصناعي المحبب وخشب Vanta Ash الأصلي',
          },
          type: 'interior',
        },
        io06xfbcm17q: {
          label: {
            en: 'Heated and Ventilated Seats',
            ar: 'مقاعد قابلة للتدفئة والتهوية',
          },
          image: 'images/2379/AT4X/Heated_and_Ventilated Seats.jpg',
          title: {
            en: 'Heated and Ventilated Seats',
            ar: 'مقاعد قابلة للتدفئة والتهوية',
          },
          desc: {
            en: 'Heated and Ventilated 16-way power front seats with massage',
            ar: 'مقاعد أمامية كهربائية قابلة للتعديل في 16 وضعية، مزودة بنظام تدفئة وتهوية ونظام تدليك.',
          },
          type: 'interior',
        },
        awla0dgc0vgs: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2379/AT4X/Bose_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '12-Speaker Bose® Premium series with Centerpoint® surround sound',
            ar: 'سلسلة Bose® Premium المكونة من 12 مكبر صوت مع صوت محيطي Centerpoint®',
          },
          type: 'interior',
        },
        awla0drrc0vgs: {
          label: {
            en: 'Driving modes',
            ar: 'أوضاع القيادة',
          },
          image: 'images/2379/AT4X/Driving_modes.jpg',
          title: {
            en: 'Driving modes',
            ar: 'أوضاع القيادة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '3mdpbwhqmsv1': {
          label: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          image: 'images/2379/AT4/LED_Fog_Lamps.jpg',
          title: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '3mdpbwhqmsv1-at4-regular-cab': {
          label: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          image: 'images/2379/AT4/LED_Fog_Lamps_rc.jpg',
          title: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        g7w0pdbil08m: {
          label: {
            en: '18" Machined Aluminum Wheels',
            ar: 'عجلات ألومنيوم مشغولة الياً مقاس 18 إنش',
          },
          image: 'images/2379/AT4/Wheels.jpg',
          title: {
            en: '18" Machined Aluminum Wheels',
            ar: 'عجلات ألومنيوم مشغولة الياً مقاس 18 إنش',
          },
          desc: {
            en: '18" Machined Aluminum Wheels with Goodyear Duratec® Tires',
            ar: 'عجلات من الألومنيوم المشغول آلياً مقاس 18 إنش مع إطارات Goodyear Duratec®.',
          },
          type: 'exterior',
        },
        'g7w0pdbil08m-at4-regular-cab': {
          label: {
            en: '18" Machined Aluminum Wheels',
            ar: 'عجلات ألومنيوم مشغولة الياً مقاس 18 إنش',
          },
          image: 'images/2379/AT4/Wheels_rc.jpg',
          title: {
            en: '18" Machined Aluminum Wheels',
            ar: 'عجلات ألومنيوم مشغولة الياً مقاس 18 إنش',
          },
          desc: {
            en: '18" Machined Aluminum Wheels with Goodyear Duratec® Tires',
            ar: 'عجلات من الألومنيوم المشغول آلياً مقاس 18 إنش مع إطارات Goodyear Duratec®.',
          },
          type: 'exterior',
        },
        '7cl358iacwqj': {
          label: {
            en: 'Hooks',
            ar: 'حلقات سحب',
          },
          image: 'images/2379/AT4/Front_Hooks.jpg',
          title: {
            en: 'Hooks',
            ar: 'حلقات سحب',
          },
          desc: {
            en: 'Red recovery hooks, and dark, distinguished grille and details that creats the bold look of the AT4',
            ar: 'حلقات سحب حمراء، وشبك أمامي مميز داكن اللون، وتفاصيل أخرى تُضفي على طراز AT4 مظهراً جريئاً.',
          },
          type: 'exterior',
        },
        '7cl358iacwqj-at4-regular-cab': {
          label: {
            en: 'Hooks',
            ar: 'حلقات سحب',
          },
          image: 'images/2379/AT4/Front_Hooks_rc.jpg',
          title: {
            en: 'Hooks',
            ar: 'حلقات سحب',
          },
          desc: {
            en: 'Red recovery hooks, and dark, distinguished grille and details that creats the bold look of the AT4',
            ar: 'حلقات سحب حمراء، وشبك أمامي مميز داكن اللون، وتفاصيل أخرى تُضفي على طراز AT4 مظهراً جريئاً.',
          },
          type: 'exterior',
        },
        e31dfeagcaee09: {
          label: {
            en: '2" Suspension Lift',
            ar: 'رفع نظام التعليق بمقدار 2 إنش',
          },
          image: 'images/2379/AT4/2_inch_Suspension_Lift.jpg',
          title: {
            en: '2" Suspension Lift',
            ar: 'رفع نظام التعليق بمقدار 2 إنش',
          },
          desc: {
            en: '2" factory lift with off-road suspension and MULTIMATIC DSSV shocks',
            ar: 'رفع مصنعي 2 إنش مع تعليق للطرق الوعرة وممتصات صدمات MULTIMATIC  DSSV',
          },
          type: 'exterior',
        },
        'e31dfeagcaee09-at4-regular-cab': {
          label: {
            en: '2" Suspension Lift',
            ar: 'رفع نظام التعليق بمقدار 2 إنش',
          },
          image: 'images/2379/AT4/2_inch_Suspension_Lift_rc.jpg',
          title: {
            en: '2" Suspension Lift',
            ar: 'رفع نظام التعليق بمقدار 2 إنش',
          },
          desc: {
            en: '2" factory lift with off-road suspension and MULTIMATIC DSSV shocks',
            ar: 'رفع مصنعي 2 إنش مع تعليق للطرق الوعرة وممتصات صدمات MULTIMATIC  DSSV',
          },
          type: 'exterior',
        },
        c9gqpi62g18z: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/AT4/Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        'c9gqpi62g18z-at4-regular-cab': {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/AT4/Infoscreen_rc.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        tsxkgkfoxdo6: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/AT4/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        'tsxkgkfoxdo6-at4-regular-cab': {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/AT4/Speedometer_rc.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        muey3bbku37k: {
          label: {
            en: 'Unique Jet Black Interior with Kalahari Accents',
            ar: 'تصميم داخلي فريد باللون الأسود الداكن مع لمسات بلون كالاهاري',
          },
          image: 'images/2379/AT4/Interior.jpg',
          title: {
            en: 'Unique Jet Black Interior with Kalahari Accents',
            ar: 'تصميم داخلي فريد باللون الأسود الداكن مع لمسات بلون كالاهاري',
          },
          desc: {
            en: 'Unique Jet Black interior with Kalahari accents and heated and ventilated perforated front leather seating surfaces',
            ar: 'مقصورة داخلية فريدة باللون الأسود الداكن مع لمسات بلون كالاهاري ومقاعد أمامية من الجلد المثقب قابلة للتدفئة والتهوية',
          },
          type: 'interior',
        },
        'muey3bbku37k-at4-regular-cab': {
          label: {
            en: 'Unique Jet Black Interior with Kalahari Accents',
            ar: 'تصميم داخلي فريد باللون الأسود الداكن مع لمسات بلون كالاهاري',
          },
          image: 'images/2379/AT4/Interior_rc.jpg',
          title: {
            en: 'Unique Jet Black Interior with Kalahari Accents',
            ar: 'تصميم داخلي فريد باللون الأسود الداكن مع لمسات بلون كالاهاري',
          },
          desc: {
            en: 'Unique Jet Black interior with Kalahari accents and heated and ventilated perforated front leather seating surfaces',
            ar: 'مقصورة داخلية فريدة باللون الأسود الداكن مع لمسات بلون كالاهاري ومقاعد أمامية من الجلد المثقب قابلة للتدفئة والتهوية',
          },
          type: 'interior',
        },
        z7m52iyopf8c: {
          label: {
            en: 'Auto Trac 2-Speed Transfer Case',
            ar: 'علبة نقل الحركة ثنائية السرعات من نوع Auto Trac',
          },
          image: 'images/2379/AT4/2_Speed_Transfer.jpg',
          title: {
            en: 'Heated and Ventilated Seats',
            ar: 'مقاعد قابلة للتدفئة والتهوية',
          },
          desc: {
            en: 'AUTO TRAC 2 speed transfer case, Automatic locking rear diffrential',
            ar: 'علبة نقل حركة أوتوماتيكية ثنائية السرعات من نوع AUTO TRAC، ترس تفاضلي خلفي قابل للقفل أوتوماتيكياً',
          },
          type: 'interior',
        },
        'z7m52iyopf8c-at4-regular-cab': {
          label: {
            en: 'Auto Trac 2-Speed Transfer Case',
            ar: 'علبة نقل الحركة ثنائية السرعات من نوع Auto Trac',
          },
          image: 'images/2379/AT4/2_Speed_Transfer_rc.jpg',
          title: {
            en: 'Heated and Ventilated Seats',
            ar: 'مقاعد قابلة للتدفئة والتهوية',
          },
          desc: {
            en: 'AUTO TRAC 2 speed transfer case, Automatic locking rear diffrential',
            ar: 'علبة نقل حركة أوتوماتيكية ثنائية السرعات من نوع AUTO TRAC، ترس تفاضلي خلفي قابل للقفل أوتوماتيكياً',
          },
          type: 'interior',
        },
        aj49xu9d3hac: {
          label: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          image: 'images/2379/Ele/LED_Fog_Lamps.jpg',
          title: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'aj49xu9d3hac-elevation-regular-cab': {
          label: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          image: 'images/2379/Ele/rc_LED_Fog_Lamps.jpg',
          title: {
            en: 'LED fog lamps',
            ar: 'مصابيح الضباب LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '4r35jcvwymvo': {
          label: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          image: 'images/2379/Ele/Wheels.jpg',
          title: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          desc: {
            en: '18" 6-Spoke Machined Aluminum with dark Grey Metallic Accents',
            ar: 'عجلات ألومنيوم مصقولة مقاس 18 إنش بستة أضلاع مع لمسات باللون الرمادي المعدني الداكن',
          },
          type: 'exterior',
        },
        '4r35jcvwymvo-elevation-regular-cab': {
          label: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          image: 'images/2379/Ele/rc_Wheels.jpg',
          title: {
            en: 'Black painted aluminum wheels',
            ar: 'عجلات من الألومنيوم المطلي باللون الأسود',
          },
          desc: {
            en: '18" 6-Spoke Machined Aluminum with dark Grey Metallic Accents',
            ar: 'عجلات ألومنيوم مصقولة مقاس 18 إنش بستة أضلاع مع لمسات باللون الرمادي المعدني الداكن',
          },
          type: 'exterior',
        },
        feyns0167q1m: {
          label: {
            en: 'Front Grille with Mesh Inserts',
            ar: 'شبك أمامي مع حشوات شبكية',
          },
          image: 'images/2379/Ele/Front_Grille.jpg',
          title: {
            en: 'Front Grille with Mesh Inserts',
            ar: 'شبك أمامي مع حشوات شبكية',
          },
          desc: {
            en: 'Monochromatic exterior with gloss-black grille',
            ar: 'تصميم خارجي أحادي اللون مع شبك أمامي أسود لامع',
          },
          type: 'exterior',
        },
        'feyns0167q1m-elevation-regular-cab': {
          label: {
            en: 'Front Grille with Mesh Inserts',
            ar: 'شبك أمامي مع حشوات شبكية',
          },
          image: 'images/2379/Ele/rc_Front_Grille.jpg',
          title: {
            en: 'Front Grille with Mesh Inserts',
            ar: 'شبك أمامي مع حشوات شبكية',
          },
          desc: {
            en: 'Monochromatic exterior with gloss-black grille',
            ar: 'تصميم خارجي أحادي اللون مع شبك أمامي أسود لامع',
          },
          type: 'exterior',
        },
        '3a79ezcnnbft': {
          label: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          image: 'images/2379/Ele/Bed.jpg',
          title: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          desc: {
            en: 'Expansive volume and innovative features help you carry the load in your light-duty truck.',
            ar: 'يساعدك الحجم الكبير والميزات المبتكرة على حمل الأمتعة في شاحنتك الخفيفة.',
          },
          type: 'exterior',
        },
        '3a79ezcnnbft-elevation-regular-cab': {
          label: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          image: 'images/2379/Ele/rc_Bed.jpg',
          title: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          desc: {
            en: 'Expansive volume and innovative features help you carry the load in your light-duty truck.',
            ar: 'يساعدك الحجم الكبير والميزات المبتكرة على حمل الأمتعة في شاحنتك الخفيفة.',
          },
          type: 'exterior',
        },
        zfyfm5c98h57: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/Ele/Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        'zfyfm5c98h57-elevation-regular-cab': {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/Ele/rc_Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        '7mzvb9y21623': {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/Ele/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        '7mzvb9y21623-elevation-regular-cab': {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/Ele/rc_Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        '56b99wbs39ki': {
          label: {
            en: 'Distinct Wheels',
            ar: 'عجلات مميزة',
          },
          image: 'images/2379/SLE/Wheels.jpg',
          title: {
            en: 'Distinct Wheels',
            ar: 'عجلات مميزة',
          },
          desc: {
            en: '18" 6-spoke machined aluminium with dark grey metallic accent, standard on SLE',
            ar: 'عجلات ألومنيوم مصقولة مقاس 18 إنش بستة أضلاع مع لمسات معدنية باللون الرمادي الداكن، متوفرة بشكل قياسي في طراز SLE.',
          },
          type: 'exterior',
        },
        '56b99wbs39ki-sle-regular-cab': {
          label: {
            en: 'Distinct Wheels',
            ar: 'عجلات مميزة',
          },
          image: 'images/2379/SLE/rc_Wheels.jpg',
          title: {
            en: 'Distinct Wheels',
            ar: 'عجلات مميزة',
          },
          desc: {
            en: '18" 6-spoke machined aluminium with dark grey metallic accent, standard on SLE',
            ar: 'عجلات ألومنيوم مصقولة مقاس 18 إنش بستة أضلاع مع لمسات معدنية باللون الرمادي الداكن، متوفرة بشكل قياسي في طراز SLE.',
          },
          type: 'exterior',
        },
        slfnh3hjd6s8: {
          label: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم',
          },
          image: 'images/2379/SLE/Front_Bumper.jpg',
          title: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم',
          },
          desc: {
            en: 'Available details like the chrome front and rear bumpers with corner steps elevate the Sierra 1500’s already remarkable exterior.',
            ar: 'تفاصيل متوفّرة مثل مصدّات الكروم الأمامية والخلفية مع عتبات زاوية ترتقي بالمظهر الخارجي المميّز أصلاً لطراز سييرا 1500.',
          },
          type: 'exterior',
        },
        'slfnh3hjd6s8-sle-regular-cab': {
          label: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم',
          },
          image: 'images/2379/SLE/rc_Front_Bumper.jpg',
          title: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم',
          },
          desc: {
            en: 'Available details like the chrome front and rear bumpers with corner steps elevate the Sierra 1500’s already remarkable exterior.',
            ar: 'تفاصيل متوفّرة مثل مصدّات الكروم الأمامية والخلفية مع عتبات زاوية ترتقي بالمظهر الخارجي المميّز أصلاً لطراز سييرا 1500.',
          },
          type: 'exterior',
        },
        e31dfagcaee09: {
          label: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          image: 'images/2379/SLE/Bed.jpg',
          title: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          desc: {
            en: 'Expansive volume and innovative features help you carry the load in your light-duty truck.',
            ar: 'يساعدك الحجم الكبير والميزات المبتكرة على حمل الأمتعة في شاحنتك الخفيفة.',
          },
          type: 'exterior',
        },
        'e31dfagcaee09-sle-regular-cab': {
          label: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          image: 'images/2379/SLE/rc_Bed.jpg',
          title: {
            en: 'High-Strength Steel Bed',
            ar: 'صندوق من الفولاذ عالي المتانة',
          },
          desc: {
            en: 'Expansive volume and innovative features help you carry the load in your light-duty truck.',
            ar: 'يساعدك الحجم الكبير والميزات المبتكرة على حمل الأمتعة في شاحنتك الخفيفة.',
          },
          type: 'exterior',
        },
        av8ukajwzjsm: {
          label: {
            en: 'Cargo Tie Downs',
            ar: 'أحزمة تثبيت الحمولة',
          },
          image: 'images/2379/SLE/Cargo_Tie_Downs.jpg',
          title: {
            en: 'Cargo Tie Downs',
            ar: 'أحزمة تثبيت الحمولة',
          },
          desc: {
            en: '12 Cargo Tie Downs in Bed Corners, allowas for multiple load securment options',
            ar: '12 نقطة تثبيت للحمولة في زوايا الصندوق، مما يتيح خيارات متعددة لتأمين الحمولة.',
          },
          type: 'exterior',
        },
        'av8ukajwzjsm-sle-regular-cab': {
          label: {
            en: 'Cargo Tie Downs',
            ar: 'أحزمة تثبيت الحمولة',
          },
          image: 'images/2379/SLE/rc_Cargo_Tie_Downs.jpg',
          title: {
            en: 'Cargo Tie Downs',
            ar: 'أحزمة تثبيت الحمولة',
          },
          desc: {
            en: '12 Cargo Tie Downs in Bed Corners, allowas for multiple load securment options',
            ar: '12 نقطة تثبيت للحمولة في زوايا الصندوق، مما يتيح خيارات متعددة لتأمين الحمولة.',
          },
          type: 'exterior',
        },
        e31dagcaeee09: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/SLE/Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        'e31dagcaeee09-sle-regular-cab': {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2379/SLE/rc_Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4" Diagonal Premium GMC Infotainment System With Available Google Built in Capacity',
            ar: 'نظام معلومات وترفيه GMC ممتاز 13.4 إنش مع توفر خدمات جوجل المدمجة',
          },
          type: 'interior',
        },
        e31dagccaeee09: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/SLE/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        'e31dagccaeee09-sle-regular-cab': {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2379/SLE/rc_Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3" Diagonal Multicolor Driver Information System',
            ar: 'نظام معلومات السائق بشاشة متعددة الألوان مقاس 12.3 إنش',
          },
          type: 'interior',
        },
      },
      accessories: {
        oxqwgswyt5gb: {
          label: {
            en: 'Denali_Ultimate_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        w0vis6o69vtu: {
          label: {
            en: 'Denali_Ultimate_Short_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '57psr5kkkly3': {
          label: {
            en: 'Floor_Mat_Carpet',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '332xvlecu8zd': {
          label: {
            en: 'Tailgate_Pro_ULT_Denali',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
        },
        oxydwt1p6q9f: {
          label: {
            en: 'Floor Mat Liners',
            ar: 'دواسات أرضية واقية',
          },
          image: 'images/2379/Denali_Ultimate/DU_Floor_Mat_Liners.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC179',
        },
        '98uc9rc3z96q': {
          label: {
            en: 'Standard Tailgate with Power Up/Down Function',
            ar: 'باب خلفي قياسي مع وظيفة الفتح والإغلاق الكهربائي',
          },
          image: 'images/2379/Denali_Ultimate/DU_Tailgate_Std_ULT_Denali.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QT5',
          brandItemCode: 'AC219',
        },
        gg4svo5rq6za: {
          label: {
            en: 'GMC MultiPro Power Steps',
            ar: 'دواسات GMC MultiPro الكهربائية',
          },
          image: 'images/2379/Denali_Ultimate/GMC_MultiPro_Power_Steps.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC185',
        },
        jfprn5ll1q01: {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        gisdi9lmd3sl: {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ACC_Bed_Mat_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        qpcnnd9zo4jh: {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ACC_Illuminated_GMC_Logo_Black_DENALI.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        '9rpr1yy99434': {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ACC_Led_Puddle_Light_GMC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        hhbzhza95ou3: {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ACC_Pedal_Covers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        hhl4itlcpms4: {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/ACC_Floor_Liners_CC_GMC_ULT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        '8mc6jgrcw8a6': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        agc75a70vder: {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        '8kdvr886cyd5': {
          label: {
            en: 'Bed_Carbon_Pro',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'E3Z',
        },
        ist2outtttdgpfud: {
          label: {
            en: 'Steel Cargo Bed with Bedliner',
            ar: 'صندوق أمتعة فولاذي مع بطانة داخلية',
          },
          image: 'images/2379/Denali_Ultimate/Bed_Liner_Spray_on.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC220',
        },
        ridxq0jjfq6j: {
          label: {
            en: 'Denali_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        yg11hrtg0w1p: {
          label: {
            en: 'Denali_Short_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        r6pzsgbyb3dd: {
          label: {
            en: 'Floor_Mat_Carpet_Brown',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        r4nmd5ed5jcz: {
          label: {
            en: 'Tailgate_Pro_Denali',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
        },
        uor1deqq6b3q: {
          label: {
            en: 'Assist_Steps_BVT',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BVT',
        },
        y0n8v4i02n1m: {
          label: {
            en: 'Standard Tailgate with Power Up/Down Function',
            ar: 'باب خلفي قياسي مع وظيفة الفتح والإغلاق الكهربائي',
          },
          image: 'images/2379/Denali/Tailgate_Std_Denali.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QT5',
          brandItemCode: 'AC219',
        },
        b5abq5t1vf20: {
          label: {
            en: 'Bed_Liner_Spray_on',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        e8gz1yuapj7p: {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        efio0cm77wbz: {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ACC_Bed_Mat_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        u6lykoko82sa: {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ACC_Illuminated_GMC_Logo_Black_DENALI.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        edpc1ixph8y0: {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ACC_Led_Puddle_Light_GMC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        vx92rnkv38x4: {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ACC_Pedal_Covers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        u4454t54sdud: {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/ACC_Floor_Liners_CC_GMC_denali.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        y22i0rjx2iqu: {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        wbjjn1z62bk2: {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        ist2outtudgpfud: {
          label: {
            en: 'Carbon Fiber Composite Bed',
            ar: 'صندوق الأمتعة من ألياف الكربون المركبة',
          },
          image: 'images/2379/Denali/Bed_Carbon_Pro.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'E3Z',
          brandItemCode: 'AC162',
        },
        ist2outuudgpfud: {
          label: {
            en: 'WO_Technology_Package_Denali',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist2outyudgpfud: {
          label: {
            en: 'W_Technology_Package_AT4_Denali',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CWM',
        },
        ist2outtpptdgpfud: {
          label: {
            en: 'Bed Liner, Spray-on',
            ar: 'بطانة صندوق بالرشّ',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        rvc7zfovo8l63: {
          label: {
            en: 'GMC MultiPro Power Steps',
            ar: 'دواسات GMC MultiPro الكهربائية',
          },
          image: 'images/2379/Denali/GMC_MultiPro_Power_Steps.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC185',
        },
        mo9mwd103t3y: {
          label: {
            en: 'AT4X_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        qzyjhtvtvyz4: {
          label: {
            en: 'AT4X_Short_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        yg2c0vrjbifv: {
          label: {
            en: 'Floor_Mat_Liners',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC179',
        },
        o0nl62zzspiq: {
          label: {
            en: 'Tailgate_Pro_AT4X',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
        },
        pgakka1noktm: {
          label: {
            en: 'Bed_Liner_Spray_on',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        o5w5w6gc2y8a: {
          label: {
            en: 'Tailgate Standard',
            ar: 'باب خلفي قياسي',
          },
          image: 'images/2379/AT4X/Tailgate_Std_AT4X.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK1',
          brandItemCode: 'AC223',
        },
        rvc7zfvo8l63: {
          label: {
            en: 'GMC MultiPro Power Steps',
            ar: 'دواسات GMC MultiPro الكهربائية',
          },
          image: 'images/2379/AT4X/GMC_MultiPro_Power_Steps.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC185',
        },
        u2lv72yhjvji: {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        '9kr3yvmlgyna': {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ACC_Bed_Mat_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        b6lmgadbw6d0: {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ACC_Illuminated_GMC_Logo_Black_AT4X_AEV.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        bfe0tnmjue5h: {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ACC_Led_Puddle_Light_GMC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        o4zttd5xeiwl: {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ACC_Pedal_Covers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        u15xauvb0ygj: {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/Shader_Floor_Liners_CC_AT4X.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        yhj2u5lbn0l6: {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        pi3b11cu1nvq: {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        ist2outhtdgpfud: {
          label: {
            en: 'WO_AEV_Parts',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist2outtodgpfud: {
          label: {
            en: 'AT4X AEV Edition',
            ar: 'إصدار AT4X AEV',
          },
          image: 'images/2379/AT4X/W_AEV_Parts.jpg',
          category: {
            en: 'AEV Edition',
            ar: 'إصدار AEV',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ULV',
          brandItemCode: 'AC146',
        },
        yxm1e6rw13fe: {
          label: {
            en: 'AT4_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        fouew54veqvs: {
          label: {
            en: 'AT4_Short_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'fouew54veqvs-at4-regular-cab': {
          label: {
            en: 'AT4_STD_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        g6fl3n7ykrq5: {
          label: {
            en: 'Floor_Mat_Rubber',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'g6fl3n7ykrq5-at4-regular-cab': {
          label: {
            en: 'Floor_Mat_Rubber',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '6yvi6vsvq7s9': {
          label: {
            en: 'Tailgate_Pro',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
        },
        '6yvi6vsvq7s9-at4-regular-cab': {
          label: {
            en: 'GMC MultiPro Tailgate',
            ar: 'باب خلفي GMC MultiPro',
          },
          image: 'images/2379/AT4/Tailgate_Pro.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC186',
        },
        '74ry84y59ibg': {
          label: {
            en: 'Premium Floor Liners with removable carpet insert, front and Rear',
            ar: 'بطانات أرضية فاخرة مع سجادة داخلية قابلة للإزالة، للأمام والخلف.',
          },
          image: 'images/2379/AT4/Floor_Mat_Liners.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC204',
        },
        '74ry84y59ibg-at4-regular-cab': {
          label: {
            en: 'Premium Floor Liners with removable carpet insert, front and Rear',
            ar: 'بطانات أرضية فاخرة مع سجادة داخلية قابلة للإزالة، للأمام والخلف.',
          },
          image: 'images/2379/AT4/Floor_Mat_Liners_rc.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC204',
        },
        zvaaxlwqe4qx: {
          label: {
            en: 'Tailgate Standard',
            ar: 'باب خلفي قياسي',
          },
          image: 'images/2379/AT4/Tailgate_Std.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK1',
          brandItemCode: 'AC223',
        },
        'zvaaxlwqe4qx-at4-regular-cab': {
          label: {
            en: 'Tailgate Standard',
            ar: 'باب خلفي قياسي',
          },
          image: 'images/2379/AT4/Tailgate_Std.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK1',
          brandItemCode: 'AC223',
        },
        kviwsh9jczd9: {
          label: {
            en: 'Machined_Aluminum_Dark_Painted_Pockets',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '57N',
        },
        'kviwsh9jczd9-at4': {
          label: {
            en: 'Machined_Aluminum_Dark_Painted_Pockets',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '57N',
        },
        wu8y5uoc1ioq: {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        'wu8y5uoc1ioq-at4-regular-cab': {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        '7o58kl7brwfj': {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ACC_Bed_Mat_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        '7o58kl7brwfj-at4-regular-cab': {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ACC_Bed_Mat_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        wh63po09fmaw: {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ACC_Illuminated_GMC_Logo_Black_SLT_AT4.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        'wh63po09fmaw-at4-regular-cab': {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ACC_Illuminated_GMC_Logo_Black_SLT_AT4_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        wbxrfr2kxwji: {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ACC_Led_Puddle_Light_GMC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        'wbxrfr2kxwji-at4-regular-cab': {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ACC_Led_Puddle_Light_GMC_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        '4suumceyxio5': {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ACC_Pedal_Covers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        '4suumceyxio5-at4-regular-cab': {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ACC_Pedal_Covers_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        dwhrgewq2lfe: {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/Shader_Floor_Liners_CC_AT4.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        'dwhrgewq2lfe-at4-regular-cab': {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/ACC_Floor_Liners_RC_Black_AT4.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        tj2jpobpx5dp: {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        'tj2jpobpx5dp-at4-regular-cab': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ACC_Hard_Folding_Tonneau_Cover_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        rxm16nzhijdr: {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        'rxm16nzhijdr-at4-regular-cab': {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ACC_Sports_Bar_Bed_Mounted_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        es6d42r8m4tu: {
          label: {
            en: 'Machined Aluminum Carbon Grey Metallic',
            ar: 'ألومنيوم مصقول بلون رمادي معدني كربوني',
          },
          image: 'images/2379/AT4/Machined_Aluminum_Carbon_Grey_Metallic.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'NZH',
          brandItemCode: 'AC196',
        },
        'es6d42r8m4tu-at4-regular-cab': {
          label: {
            en: 'Machined Aluminum Carbon Grey Metallic',
            ar: 'ألومنيوم مصقول بلون رمادي معدني كربوني',
          },
          image: 'images/2379/AT4/Machined_Aluminum_Carbon_Grey_Metallic_rc.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'NZH',
          brandItemCode: 'AC196',
        },
        ist2ouuttdgpfud: {
          label: {
            en: 'Steel_Cargo_bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist2ouuttdgpfud-at4-regular-cab': {
          label: {
            en: 'Steel_Cargo_bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist2ouuptdgpfud: {
          label: {
            en: 'Bed Liner, Spray-on',
            ar: 'بطانة صندوق بالرشّ',
          },
          image: 'images/2379/AT4/Bed_Liner_Spray_on.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        'ist2ouuptdgpfud-at4-regular-cab': {
          label: {
            en: 'Bed Liner, Spray-on',
            ar: 'بطانة صندوق بالرشّ',
          },
          image: 'images/2379/AT4/Bed_Liner_Spray_on_rc.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        ist2otuptdgpfud: {
          label: {
            en: '5.3L_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L84',
        },
        'ist2otuptdgpfud-at4-regular-cab': {
          label: {
            en: '5.3L_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L84',
        },
        ist2omuptygpfud: {
          label: {
            en: 'Engine, 6.2L EcoTec3 V8',
            ar: 'المحرك، 6.2 لتر EcoTec3 V8',
          },
          image: 'images/2379/AT4/6.2L_Engine.jpg',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L87',
          brandItemCode: 'AC37',
        },
        'ist2omuptygpfud-at4-regular-cab': {
          label: {
            en: 'Engine, 6.2L EcoTec3 V8',
            ar: 'المحرك، 6.2 لتر EcoTec3 V8',
          },
          image: 'images/2379/AT4/6.2L_Engine.jpg',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L87',
          brandItemCode: 'AC37',
        },
        ist2omnuptygpfud: {
          label: {
            en: 'Standard_Roof_AT4',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist2omnupptygpfud: {
          label: {
            en: 'Power Sunroof',
            ar: 'فتحة سقف كهربائية',
          },
          image: 'images/2379/AT4/Sunroof_AT4.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CF5',
          brandItemCode: 'AC202',
        },
        ist2onnupptygpfud: {
          label: {
            en: 'WO_Technology_Package_Denali',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist2onnpptygpfud: {
          label: {
            en: 'W_Technology_Package_AT4_Denali',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CWM',
        },
        '8s2wi4e4vhfs': {
          label: {
            en: 'Elevation_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '9q3c75v5lpjh': {
          label: {
            en: 'Elevation_Short_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '9q3c75v5lpjh-elevation-regular-cab': {
          label: {
            en: 'Elevation_STD_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ctiiaau8rxez: {
          label: {
            en: 'Floor_Mat_Rubber_Brown',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ctiiaau8rxez-elevation-regular-cab': {
          label: {
            en: 'Floor_Mat_Rubber_Brown',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '2thmtg046cnh': {
          label: {
            en: 'Tailgate_Std',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK1',
        },
        '2thmtg046cnh-elevation': {
          label: {
            en: 'Tailgate_Std',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK1',
        },
        w6212zibw7u2: {
          label: {
            en: 'GMC MultiPro Tailgate',
            ar: 'باب خلفي GMC MultiPro',
          },
          image: 'images/2379/Ele/Tailgate_Pro.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC186',
        },
        'w6212zibw7u2-elevation-regular-cab': {
          label: {
            en: 'GMC MultiPro Tailgate',
            ar: 'باب خلفي GMC MultiPro',
          },
          image: 'images/2379/Ele/rc_Tailgate_Pro.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC186',
        },
        y8ymiarprmv4: {
          label: {
            en: 'Turbomax_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L3B',
        },
        'y8ymiarprmv4-elevation-regular-cab': {
          label: {
            en: 'Turbomax_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L3B',
        },
        d5a3wq648mre: {
          label: {
            en: '5.3L_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L84',
        },
        'd5a3wq648mre-elevation-regular-cab': {
          label: {
            en: '5.3L_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L84',
        },
        rx3fxh4w00fa: {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        'rx3fxh4w00fa-elevation-regular-cab': {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        mwe92ojlfc9p: {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ACC_Bed_Mat_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        'mwe92ojlfc9p-elevation-regular-cab': {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ele_ACC_Bed_Mat_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        '3sjpe1de9fow': {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ACC_Illuminated_GMC_Logo_Black_SLE_ELEVATION.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        '3sjpe1de9fow-elevation-regular-cab': {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ele_ACC_Illuminated_GMC_Logo_Black_SLE_ELE_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        xr3j6cnduod7: {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ACC_Led_Puddle_Light_GMC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        'xr3j6cnduod7-elevation-regular-cab': {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ele_ACC_Led_Puddle_Light_GMC_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        '6ed04e5xsebb': {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ACC_Pedal_Covers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        '6ed04e5xsebb-elevation-regular-cab': {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ele_ACC_Pedal_Covers_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        lqug7is1eblt: {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/ACC_Floor_Liners_CC_GMC_sle_ELE.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        'lqug7is1eblt-elevation-regular-cab': {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/ele_ACC_Floor_Liners_RC_Black_ELE.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        '7w4207uu0x8f': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        '7w4207uu0x8f-elevation-regular-cab': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ele_ACC_Hard_Folding_Tonneau_Cover_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        hadg99yjs0k1: {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        'hadg99yjs0k1-elevation-regular-cab': {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ele_ACC_Sports_Bar_Bed_Mounted_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        ist22outtdgpfud: {
          label: {
            en: 'WO_Bed_View_Camera',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist22outtdgpfud-elevation-regular-cab': {
          label: {
            en: 'WO_Bed_View_Camera',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist23outtdgpfud: {
          label: {
            en: 'Bed View Camera',
            ar: 'كاميرا لرؤية الصندوق',
          },
          image: 'images/2379/Ele/W_Bed_View_Camera.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UVN',
          brandItemCode: 'AC151',
        },
        'ist23outtdgpfud-elevation-regular-cab': {
          label: {
            en: 'Bed View Camera',
            ar: 'كاميرا لرؤية الصندوق',
          },
          image: 'images/2379/Ele/W_Bed_View_Camera.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UVN',
          brandItemCode: 'AC151',
        },
        ist23ouuttdgpfud: {
          label: {
            en: 'Machined_Aluminum_Dark_Painted_Pockets',
            ar: '',
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '57N',
        },
        'ist23ouuttdgpfud-elevation': {
          label: {
            en: 'Machined_Aluminum_Dark_Painted_Pockets',
            ar: '',
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '57N',
        },
        ist21ouuptdgpfud: {
          label: {
            en: '6 Spoke Machined Aluminum',
            ar: 'عجلات ألومنيوم مشغولة آلياً بستة أضلاع',
          },
          image: 'images/2379/Ele/6_Spoke_Machined_Aluminum.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UHN',
          brandItemCode: 'AC139',
        },
        'ist21ouuptdgpfud-elevation-regular-cab': {
          label: {
            en: '6 Spoke Machined Aluminum',
            ar: 'عجلات ألومنيوم مشغولة آلياً بستة أضلاع',
          },
          image: 'images/2379/Ele/rc_6_Spoke_Machined_Aluminum.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UHN',
          brandItemCode: 'AC139',
        },
        ist29ouuptdgpfud: {
          label: {
            en: 'Black Gloss Painted Aluminium',
            ar: 'ألومنيوم مطلي باللون الأسود اللامع',
          },
          image: 'images/2379/Ele/Black_Gloss_Painted_Aluminium.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RD3',
          brandItemCode: 'AC153',
        },
        'ist29ouuptdgpfud-elevation-regular-cab': {
          label: {
            en: 'Black Gloss Painted Aluminium',
            ar: 'ألومنيوم مطلي باللون الأسود اللامع',
          },
          image: 'images/2379/Ele/rc_Black_Gloss_Painted_Aluminium.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RD3',
          brandItemCode: 'AC153',
        },
        ist39ouuptdgpfud: {
          label: {
            en: 'Steel_Cargo_bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist39ouuptdgpfud-elevation': {
          label: {
            en: 'Steel_Cargo_bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist49ouuptdgpfud: {
          label: {
            en: 'Bed Liner, Spray-on',
            ar: 'بطانة صندوق بالرشّ',
          },
          image: 'images/2379/Ele/Bed_Liner_Spray_on.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        'ist49ouuptdgpfud-elevation-regular-cab': {
          label: {
            en: 'Bed Liner, Spray-on',
            ar: 'بطانة صندوق بالرشّ',
          },
          image: 'images/2379/Ele/rc_Bed_Liner_Spray_on.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        t7wjlog6bvl5: {
          label: {
            en: 'WO_Heated_Seat_Steering',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        't7wjlog6bvl5-elevation-regular-cab': {
          label: {
            en: 'WO_Heated_Seat_Steering',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        isu929opuptdgpfu2: {
          label: {
            en: 'Seats, front bucket with center console',
            ar: 'مقاعد أمامية منفصلة مع كنسولة مركزية',
          },
          image: 'images/2379/Ele/Bucket_Console_Cloth.jpg',
          category: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'A50',
          brandItemCode: 'AC209',
        },
        'isu929opuptdgpfu2-elevation-regular-cab': {
          label: {
            en: 'Seats, front bucket with center console',
            ar: 'مقاعد أمامية منفصلة مع كنسولة مركزية',
          },
          image: 'images/2379/Ele/Bucket_Console_Cloth.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'A50',
          brandItemCode: 'AC209',
        },
        v2vjnw0hmy80: {
          label: {
            en: 'Heated Front Seats and Steering Wheel',
            ar: 'مقاعد أمامية وعجلة قيادة قابلة للتدفئة',
          },
          image: 'images/2379/Ele/W_Heated_Seat_Steering.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'KQV',
          brandItemCode: 'AC82',
        },
        'v2vjnw0hmy80-elevation-regular-cab': {
          label: {
            en: 'Heated Front Seats and Steering Wheel',
            ar: 'مقاعد أمامية وعجلة قيادة قابلة للتدفئة',
          },
          image: 'images/2379/Ele/rc_W_Heated_Seat_Steering.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'KQV',
          brandItemCode: 'AC82',
        },
        ist79ouuptdgpfud: {
          label: {
            en: 'Bose Sound System, premium 7- speaker system with Richbass woofer',
            ar: 'نظام صوتي من Bose، نظام صوتي ممتاز من 7 مكبرات صوت مع مضخم صوت Richbass',
          },
          image: 'images/2379/Ele/Bose_Speakers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UQA',
          brandItemCode: 'AC159',
        },
        'ist79ouuptdgpfud-elevation-regular-cab': {
          label: {
            en: 'Bose Sound System, premium 7- speaker system with Richbass woofer',
            ar: 'نظام صوتي من Bose، نظام صوتي ممتاز من 7 مكبرات صوت مع مضخم صوت Richbass',
          },
          image: 'images/2379/Ele/Bose_Speakers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UQA',
          brandItemCode: 'AC159',
        },
        ctjy9mi8svfw: {
          label: {
            en: 'Assist steps, Chrome wheel to wheel',
            ar: 'عتبات مساعدة من الكروم من عجلة إلى عجلة',
          },
          image: 'images/2379/Ele/Assist_Steps_BVT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BVT',
          brandItemCode: 'AC76',
        },
        'ctjy9mi8svfw-elevation-regular-cab': {
          label: {
            en: 'Assist steps, Chrome wheel to wheel',
            ar: 'عتبات مساعدة من الكروم من عجلة إلى عجلة',
          },
          image: 'images/2379/Ele/Assist_Steps_BVT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BVT',
          brandItemCode: 'AC76',
        },
        ist199ouuptdgpfud: {
          label: {
            en: 'WO_Z71_Suspension_Package_ELE',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        isu99ouuptdgpfud: {
          label: {
            en: 'W_Z71_Suspension_Package_ELE',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'X31',
        },
        isu929opuptdgpfud: {
          label: {
            en: 'Driver_Alert_Package_ELE',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        isu929opuptdgpfu1: {
          label: {
            en: 'Seats, front bucket with center console default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '3w06jsi5oinw': {
          label: {
            en: 'SLE_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        whv4p5vjna3p: {
          label: {
            en: 'SLE_Short_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'whv4p5vjna3p-sle-regular-cab': {
          label: {
            en: 'SLE_STD_Bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        dpsosp5gxrx3: {
          label: {
            en: 'Floor_Mat_Rubber_Brown',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'dpsosp5gxrx3-sle-regular-cab': {
          label: {
            en: 'Floor_Mat_Rubber_Brown',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        prnt5nsa6jxn: {
          label: {
            en: 'Tailgate_Std',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK1',
        },
        '9kq6x8627fks': {
          label: {
            en: '6_Spoke_Machined_Aluminum',
            ar: 'ألومنيوم مشغول آلياً بـ 6 قضبان',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC139',
        },
        '9kq6x8627fks-sle': {
          label: {
            en: '6_Spoke_Machined_Aluminum',
            ar: 'ألومنيوم مشغول آلياً بـ 6 قضبان',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC139',
        },
        isd8oqmgpfud: {
          label: {
            en: 'GMC MultiPro Tailgate',
            ar: 'باب خلفي GMC MultiPro',
          },
          image: 'images/2379/SLE/rc_Tailgate_Pro.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC186',
        },
        isd8oqfmgpfud: {
          label: {
            en: 'Polished Aluminum Technical Grey Painted',
            ar: 'ألومنيوم مصقول مطلي بلون رمادي Technical Grey',
          },
          image: 'images/2379/SLE/Polished_Aluminum_Technical_Grey_Painted.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'NZD',
          brandItemCode: 'AC200',
        },
        'isd8oqfmgpfud-sle-regular-cab': {
          label: {
            en: 'Polished Aluminum Technical Grey Painted',
            ar: 'ألومنيوم مصقول مطلي بلون رمادي Technical Grey',
          },
          image: 'images/2379/SLE/rc_Polished_Aluminum_Technical_Grey_Painted.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'NZD',
          brandItemCode: 'AC200',
        },
        isd8oqfmdgpfud: {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        'isd8oqfmdgpfud-sle-regular-cab': {
          label: {
            en: 'Assist Steps 4 inch Round, Black',
            ar: 'دواسات مساعدة دائرية الشكل مقاس 4 إنش، أسود',
          },
          image: 'images/2379/Acc/ACC_Assist_Steps_CC_4in_Round_Black.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        isd8oqftdgpfud: {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/ACC_Bed_Mat_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        'isd8oqftdgpfud-sle-regular-cab': {
          label: {
            en: 'Bed Mat, Black',
            ar: 'بطانة صندوق، أسود',
          },
          image: 'images/2379/Acc/sle_ACC_Bed_Mat_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC72',
        },
        isd8oqytdgpfud: {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/ACC_Illuminated_GMC_Logo_Black_SLE_ELEVATION.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        'isd8oqytdgpfud-sle-regular-cab': {
          label: {
            en: 'Illumintaed GMC Logo, Black',
            ar: 'شعار GMC مضيء، أسود',
          },
          image: 'images/2379/Acc/sle_ACC_Illuminated_GMC_Logo_Black_SLE_ELE_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RN2',
          brandItemCode: 'AC192',
        },
        ist8oqytdgpfud: {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/ACC_Led_Puddle_Light_GMC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        'ist8oqytdgpfud-sle-regular-cab': {
          label: {
            en: 'LED Puddle Lights',
            ar: 'مصابيح LED للإضاءة السفلية عند الأبواب',
          },
          image: 'images/2379/Acc/sle_ACC_Led_Puddle_Light_GMC_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'S3I',
          brandItemCode: 'AC195',
        },
        o4zovzzvc244: {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/ACC_Pedal_Covers.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        'o4zovzzvc244-sle-regular-cab': {
          label: {
            en: 'Sport Pedal Covers',
            ar: 'أغطية دواسات رياضية',
          },
          image: 'images/2379/Acc/sle_ACC_Pedal_Covers_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBZ',
          brandItemCode: 'AC216',
        },
        ist8outtdgpfud: {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/ACC_Floor_Liners_CC_GMC_sle_ELE.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        'ist8outtdgpfud-sle-regular-cab': {
          label: {
            en: 'Floor Liners, Black',
            ar: 'بطانات أرضية، أسود',
          },
          image: 'images/2379/Acc/sle_ACC_Floor_Liners_RC_Black_SLE.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC177',
        },
        ist6outtdgpfud: {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        'ist6outtdgpfud-sle-regular-cab': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صندوق الأمتعة القابل للطي الصلب',
          },
          image: 'images/2379/Acc/sle_ACC_Hard_Folding_Tonneau_Cover_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VOZ',
          brandItemCode: 'AC73',
        },
        ist2outtdgpfud: {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        'ist2outtdgpfud-sle-regular-cab': {
          label: {
            en: 'Sports Bar - Bed mounted',
            ar: 'قوس رياضي مثبّت على الصندوق',
          },
          image: 'images/2379/Acc/sle_ACC_Sports_Bar_Bed_Mounted_RC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        ist21outtdgpfud: {
          label: {
            en: 'WO_Bed_View_Camera',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist21outtdgpfud-sle-regular-cab': {
          label: {
            en: 'WO_Bed_View_Camera',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist211outtdgpfud: {
          label: {
            en: 'Bed View Camera',
            ar: 'كاميرا لرؤية الصندوق',
          },
          image: 'images/2379/SLE/W_Bed_View_Camera.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UVN',
          brandItemCode: 'AC151',
        },
        'ist211outtdgpfud-sle-regular-cab': {
          label: {
            en: 'Bed View Camera',
            ar: 'كاميرا لرؤية الصندوق',
          },
          image: 'images/2379/SLE/W_Bed_View_Camera.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UVN',
          brandItemCode: 'AC151',
        },
        ist31outtdgpfud: {
          label: {
            en: 'Turbomax_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L3B',
        },
        'ist31outtdgpfud-sle-regular-cab': {
          label: {
            en: 'Turbomax_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L3B',
        },
        ist41outtdgpfud: {
          label: {
            en: '5.3L_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L84',
        },
        'ist41outtdgpfud-sle-regular-cab': {
          label: {
            en: '5.3L_Engine',
            ar: '',
          },
          image: '',
          category: {
            en: 'Performance',
            ar: 'الأداء',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'L84',
        },
        ist51outtdgpfud: {
          label: {
            en: 'Steel_Cargo_bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist51outtdgpfud-sle-regular-cab': {
          label: {
            en: 'Steel_Cargo_bed',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist61outtdgpfud: {
          label: {
            en: 'Bed Liner, Spray-on',
            ar: 'بطانة صندوق بالرشّ',
          },
          image: 'images/2379/SLE/Bed_Liner_Spray_on.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        'ist61outtdgpfud-sle-regular-cab': {
          label: {
            en: 'Bed Liner, Spray-on',
            ar: 'بطانة صندوق بالرشّ',
          },
          image: 'images/2379/SLE/rc_Bed_Liner_Spray_on.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC149',
        },
        ist59ouuptdgpfud: {
          label: {
            en: 'WO_Heated_Seat_Steering',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist59ouuptdgpfud-sle-regular-cab': {
          label: {
            en: 'WO_Heated_Seat_Steering',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist890ouuptdgpfud: {
          label: {
            en: 'Seats, front bucket with center console',
            ar: 'مقاعد أمامية منفصلة مع كنسولة مركزية',
          },
          image: 'images/2379/SLE/Bucket_Console_Cloth.jpg',
          category: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'A50',
          brandItemCode: 'AC209',
        },
        'ist890ouuptdgpfud-sle-regular-cab': {
          label: {
            en: 'Seats, front bucket with center console',
            ar: 'مقاعد أمامية منفصلة مع كنسولة مركزية',
          },
          image: 'images/2379/SLE/Bucket_Console_Cloth.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'A50',
          brandItemCode: 'AC209',
        },
        ist69ouuptdgpfud: {
          label: {
            en: 'Heated Front Seats and Steering Wheel',
            ar: 'مقاعد أمامية وعجلة قيادة قابلة للتدفئة',
          },
          image: 'images/2379/SLE/W_Heated_Seat_Steering.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'KQV',
          brandItemCode: 'AC82',
        },
        ist89ouuptdgpfud: {
          label: {
            en: 'Assist steps, Chrome wheel to wheel',
            ar: 'عتبات مساعدة من الكروم من عجلة إلى عجلة',
          },
          image: 'images/2379/SLE/Assist_Steps_BVT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BVT',
          brandItemCode: 'AC76',
        },
        'ist89ouuptdgpfud-sle-regular-cab': {
          label: {
            en: 'Assist steps, Chrome wheel to wheel',
            ar: 'عتبات مساعدة من الكروم من عجلة إلى عجلة',
          },
          image: 'images/2379/SLE/Assist_Steps_BVT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'BVT',
          brandItemCode: 'AC76',
        },
        ist99ouuptdgpfud: {
          label: {
            en: 'Bench_Console_Cloth',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist99ouuptdgpfud-sle-regular-cab': {
          label: {
            en: 'Bench_Console_Cloth',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ist99o1uuptdgpfud: {
          label: {
            en: 'WO_Z71_Suspension_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'ist99o1uuptdgpfud-sle-regular-cab': {
          label: {
            en: 'WO_Z71_Suspension_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        isu199ouuptdgpfud: {
          label: {
            en: 'W_Z71_Suspension_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'X31',
        },
        'isu199ouuptdgpfud-sle-regular-cab': {
          label: {
            en: 'W_Z71_Suspension_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'X31',
        },
        isu299opuptdgpfud: {
          label: {
            en: 'Driver_Alert_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'isu299opuptdgpfud-sle-regular-cab': {
          label: {
            en: 'Driver_Alert_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
      },
      packages: {
        d32e9gl7iz90: {
          label: {
            en: 'Technology Package',
            ar: 'فئة التكنولوجيا',
          },
          image: 'images/2379/Denali/W_Technology_Package_Denali.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The Technology Package comes with Rear Camera Mirror and Multicolor 15" Diagonal Head-Up Display.',
            ar: 'تتضمّن فئة التكنولوجيا مرآة الكاميرا الخلفية وشاشة عرض علوية أمامية متعددة الألوان مقاس 15 إنش.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Multicolor 15" Diagonal Head-Up Display',
                ar: 'شاشة عرض علوية أمامية متعددة الألوان مقاس 15 إنش.',
              },
              accessoryDescription: {
                en: 'Multicolor 15" Diagonal Head-Up Display',
                ar: 'شاشة عرض علوية أمامية متعددة الألوان مقاس 15 إنش.',
              },
              image: 'images/2379/Denali/W_Technology_Package_Denali_3.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية',
              },
              accessoryDescription: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية',
              },
              image: 'images/2379/Denali/W_Technology_Package_Denali_2.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        v8j6f45my7e5: {
          label: {
            en: 'Technology Package',
            ar: 'فئة التكنولوجيا',
          },
          image: 'images/2379/AT4/W_Technology_Package_AT4.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The Technology Package comes with Rear Camera Mirror, Multicolor 15" Diagonal Head-Up Display and Bed View Camera.',
            ar: 'تتضمّن باقة التكنولوجيا مرآة الرؤية الخلفية المزودة بكاميرا، وشاشة عرض علوية أمامية متعددة الألوان بقياس 15 إنش، وكاميرا لعرض صندوق الأمتعة.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Multicolor 15" Diagonal Head-Up Display',
                ar: 'شاشة عرض علوية أمامية متعددة الألوان مقاس 15 إنش.',
              },
              accessoryDescription: {
                en: 'Multicolor 15" Diagonal Head-Up Display',
                ar: 'شاشة عرض علوية أمامية متعددة الألوان مقاس 15 إنش.',
              },
              image: 'images/2379/AT4/W_Technology_Package_AT4_2.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية',
              },
              accessoryDescription: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية',
              },
              image: 'images/2379/AT4/W_Technology_Package_AT4_1.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Bed View Camera',
                ar: 'كاميرا لرؤية الصندوق',
              },
              accessoryDescription: {
                en: 'Bed View Camera',
                ar: 'كاميرا لرؤية الصندوق',
              },
              image: 'images/2379/AT4/W_Technology_Package_AT4_3.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        gwhmcx9hkln4: {
          label: {
            en: 'X31 Off Road Suspention Package',
            ar: 'حزمة نظام التعليق للطرق الوعرة X31',
          },
          image: 'images/2379/Ele/rc_W_Z71_Suspension_Package_ELE.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The x31 Off Road Suspension Package comes with Exhaust, dual with premium tips.',
            ar: 'تأتي فئة نظام التعليق للطرق الوعرة x31 مزودة بنظام عادم مزدوج مع أطراف ممتازة.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Exhaust, dual with premium tips',
                ar: 'نظام عادم مزدوج بأطراف ممتازة',
              },
              accessoryDescription: {
                en: 'Exhaust, dual with premium tips',
                ar: 'نظام عادم مزدوج بأطراف ممتازة',
              },
              image: 'images/2379/Ele/rc_W_Z71_Suspension_Package_ELE.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'X31',
          brandItemCode: 'PA33',
        },
        x7vtyriufcgr0: {
          label: {
            en: 'X31 Off Road Suspention Package',
            ar: 'حزمة نظام التعليق للطرق الوعرة X31',
          },
          image: 'images/2379/SLE/rc_W_Z71_Suspension_Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The x31 Off Road Suspension Package comes with Exhaust, dual with premium tips.',
            ar: 'تأتي فئة نظام التعليق للطرق الوعرة x31 مزودة بنظام عادم مزدوج مع أطراف ممتازة.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Exhaust, dual with premium tips',
                ar: 'نظام عادم مزدوج بأطراف ممتازة',
              },
              accessoryDescription: {
                en: 'Exhaust, dual with premium tips',
                ar: 'نظام عادم مزدوج بأطراف ممتازة',
              },
              image: 'images/2379/SLE/rc_W_Z71_Suspension_Package.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'X31',
          brandItemCode: 'PA33',
        },
        'x7vtyriufcgr0-sle': {
          label: {
            en: 'X31 Off Road Suspention Package',
            ar: 'حزمة نظام التعليق للطرق الوعرة X31',
          },
          image: 'images/2379/SLE/W_Z71_Suspension_Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The x31 Off Road Suspension Package comes with Exhaust, dual with premium tips.',
            ar: 'تأتي فئة نظام التعليق للطرق الوعرة x31 مزودة بنظام عادم مزدوج مع أطراف ممتازة.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Exhaust, dual with premium tips',
                ar: 'نظام عادم مزدوج بأطراف ممتازة',
              },
              accessoryDescription: {
                en: 'Exhaust, dual with premium tips',
                ar: 'نظام عادم مزدوج بأطراف ممتازة',
              },
              image: 'images/2379/SLE/W_Z71_Suspension_Package.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'X31',
          brandItemCode: 'PA33',
        },
      },
      interactions: {
        ywx48hyqwid8: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '2blnjd8gu2v1': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '9g9a5bgi68eq': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        '3f6680lmbg8l': {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        lt2y60dndnd0: {
          label: {
            en: 'DRL',
            ar: 'أضواء النهار DRL',
          },
          type: 'interaction',
        },
        dxynjw4qubax: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        '8eaoh15qfyy8': {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        qwl8becl9sgr: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        gjxwo8tfzu4z: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        rsofu3ns0i6i: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        ln2vk13w0eev: {
          label: {
            en: 'DRL',
            ar: 'أضواء النهار DRL',
          },
          type: 'interaction',
        },
        '26yxlulgpwuk': {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        jkzo5nb4kcu8: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '7d69b0ehn4v1': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        l3grk49kb6tp: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        '71pzl0ulzqds': {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        xli2gxmrn457: {
          label: {
            en: 'DRL',
            ar: 'أضواء النهار DRL',
          },
          type: 'interaction',
        },
        ckupcfxwja4s: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        ttgcv0vc2ypv: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        vbjrc2b3qn47: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        obp0p5qsd1a3: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        '8wx307el85j4': {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        vdknirw6a06i: {
          label: {
            en: 'DRL',
            ar: 'أضواء النهار DRL',
          },
          type: 'interaction',
        },
        ovfziv1t5iu0: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        '80whon9hvq2k': {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        wbs3r7ayeu0p: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        zrmrylhs1tvw: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        n0d9xags2ma4: {
          label: {
            en: 'DRL',
            ar: 'أضواء النهار DRL',
          },
          type: 'interaction',
        },
        efpnxgfbnbyi: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        nw7ayiomff7o: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        smjhed8evi5i: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        z50uy8f03jh7: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        '14eisbljp9pi': {
          label: {
            en: 'DRL',
            ar: 'أضواء النهار DRL',
          },
          type: 'interaction',
        },
        '1vqrly7ngbhv': {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقاعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقاعد الصف الثاني',
          },
        },
      },
      name: {
        en: 'GMC Sierra LD',
        ar: 'جي إم سي سييرا LD',
      },
      specifications: {
        heading: {
          en: 'GMC Sierra LD',
          ar: 'جي إم سي سييرا LD',
        },
        trim: {
          en: 'Denali Ultimate',
          ar: 'Denali Ultimate',
        },
        price: {
          en: '',
          ar: '',
        },
        model: 'GMC Sierra LD',
        seatingCapacity: '6',
        dimensions: {
          length: '5916 mm',
          width: '',
          height: '',
          wheelbase: '3748 mm',
          groundClearance: '',
        },
        performance: [
          {
            value: '420',
            unit: 'HP',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '624',
            unit: 'NM',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران',
            },
          },
          {
            value: '6.2L',
            unit: '',
            label: {
              en: 'V8 Engine',
              ar: 'V8 محرك',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'GMC Sierra LD',
              ar: 'GMC سييرا LD',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Onyx Black, Glacier White Tricoat, Downpour Metallic, Sterling Metallic, Titanium Rush Metallic, Coastal Dune, Summit White, Volcanic Red Tintcoat, Cardinal Red, Deep Bronze Metallic, Dynamic Blue Metallic (As per selected trim)',
              ar: 'أسود أونيكس، أبيض جليدي بثلاث طبقات، لون مطري معدني، فضي ستيرلينج معدني، تيتانيوم معدني، رمال الساحل، أبيض، أحمر بركاني بطبقتين، أحمر قرمزي، برونزي داكن معدني، أزرق ديناميكي معدني (حسب الفئة المختارة)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'الألوان الداخلية',
            },
            subtitle: {
              en: 'Atmosphere/Brownstone Forge Leather, Jet Black Perforated Leather with Kalahari Accents, Gideon/Very Dark Atmosphere Leather Seats, Gideon/Very Dark Atmosphere Cloth Seats, Dark Walnut/Slate Leather, Alpine Umber Full Grain Leather (As per selected trim)',
              ar: 'جلد Forge بيج/حجري بني، جلد أسود داكن مثقب مع لمسات كالاهاري، مقاعد جيديون/بيج داكن جداً من الجلد، مقاعد جيديون/بيج داكن جداً من القماش، جلد جوزي داكن/صخري، جلد كامل الحبيبات بلون Alpine Umber (حسب الطراز المختار)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '3748 mm',
              ar: '3748 ملم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Length',
              ar: 'الطول',
            },
            subtitle: {
              en: '5916 mm',
              ar: '5916 ملم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'حيز الأمتعة',
            },
            subtitle: {
              en: '1781.3 litres',
              ar: '1781.3 لتر',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Transmission',
              ar: 'نقل الحركة',
            },
            subtitle: {
              en: '8-speed Automatic / 10-speed Automatic',
              ar: 'ناقل حركة أوتوماتيكي بـ 8 سرعات / ناقل حركة أوتوماتيكي بـ 10 سرعات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة اونستار الأساسية',
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة اونستار ون',
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'عدد الركاب',
            },
            subtitle: {
              en: '6',
              ar: '6',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: '13.4" Premium Infotainment with Google built-in',
              ar: 'شاشة معلومات وترفيه مميزة مقاس 13.4 إنش مع خدمات جوجل المدمجة',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
        ],
      },
      accessoryCategories: {
        exterior: [
          'Roof',
          'Alloy Wheels',
        ],
        interior: [
          'Seats',
        ],
        options: [
          'Options & Accessories',
          'Performance',
          'AEV Edition',
        ],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.gmcarabia.com/ae-en/privacy',
        ar: 'https://www.gmcarabia.com/ae-ar/privacy',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my GMC Sierra LD configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my GMC Sierra LD configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My GMC Sierra LD Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom GMC Sierra LD configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my GMC Sierra LD Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream GMC Sierra LD. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '295892',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع محمد بن القاسم، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '122225',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai, Abdul Hameed Salem Sons Co, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني, شارع محمد بن القاسم, الري, شركة أبناء عبد الحميد سالم, الري',
        },
      },
      {
        id: 2,
        country: 'KW',
        value: 'KW',
        dealerId: '260852',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Omar Ibn Al Khatab Street, Sharq',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع عمر بن الخطاب، شرق',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '295854',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Nahda Main Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق النهضة الرئيسي',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260877',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Firq Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260880',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, New Industrial Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الصناعية الجديدة',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122227',
        dealerName: 'RYMCO',
        label: {
          en: 'Rasamny Younis Motor Co. SAL, Michael Zakhour Road',
          ar: 'شركة رسامني يونس للسيارات (ش.م.ل)، طريق مايكل زخور',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    showPrices: false, // Sierra LD: every price is 0 - hidden like the Cadillac linkIds
    showDisplayCarPrice: false, // no MSRP anywhere in the UI
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'sierra ld',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Sierra',
    modelName: 'Sierra',
    pageTitle: 'Sierra Configurator',
    client: 'GM',
    brand: 'gmc',
    modelId: 'GCSIET1MCM',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'gmc',
      modelName: 'sierra',
      vehicleType: 'truck',
    },
    presentation: {
      video: 'videos/2379/presentation_video_1.webm',
      loadingImage: 'images/2379/sliderImage.webp',
      transitionImage: 'images/2379/transition_logo.png',
      headerLogo: 'images/2379/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '420 HP ',
            ar: '420 حصاناً',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '624 NM',
            ar: '624 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'V8 Engine',
            ar: 'V8 محرك',
          },
          value: {
            en: '6.2L',
            ar: '6.2 لتر',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [
    {
      id: 'cabType',
      key: 'cabType',
      label: {
        en: 'Cab Type',
        ar: 'نوع المقصورة',
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: 'crew-cab',
          label: {
            en: 'Crew Cab',
            ar: 'طاقم الكابينة',
          },
          value: 'Crew Cab',
          checked: false,
        },
        {
          id: 'regular-cab',
          label: {
            en: 'Regular Cab',
            ar: 'الكابينة العادية',
          },
          value: 'Regular Cab',
          checked: false,
        },
      ],
    },
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Stratum GMC\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Stratum GMC\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#B9081B',
      '--brand-primary-hover': '#970716',
      '--brand-secondary': '#CC0000',
      '--brand-accent': '#B9081B',
      '--brand-accent-hover': '#970716',
      '--brand-accent-light': '#f7e4e9',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#B9081B',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#B9081B',
      '--brand-toggle-gradient-to': '#CC0000',
      '--brand-toggle-knob-border': '#B9081B',
      '--brand-accent-secondary': '#CC0000',
      '--brand-card-radius': '2px',
      '--brand-card-radius-sm': '2px',
      '--brand-card-radius-lg': '3px',
      '--brand-button-radius': '2px',
      '--brand-button-radius-sm': '2px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '2px',
      '--brand-button-border': 'var(--brand-accent)',
      '--brand-cta-arrow-bg': '#B9081B',
      '--brand-cta-arrow-fg': '#ffffff',
      '--brand-cta-shimmer': '#B9081B',
      '--brand-cta-loading-bg': '#B9081B',
    },
  },
} as unknown as UIMetadataComplete;
